<script setup>
  import {inject, nextTick, reactive, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDemodataDialogSelectableDemocase from "@components/Dialogs/DemodataDialog/SelectableDemocase.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentUniversalPatientAssignment from "@components/PatientSelect/UniversalPatientAssignment.vue";
  import BeateBlass from "@images/demodata/BeateBlass.png";
  import ThereseTorsade from "@images/demodata/ThereseTorsade.png";
  import UrsulaUebel from "@images/demodata/UrsulaUebel.png";
  import {router} from "@inertiajs/vue3";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "ComponentDemodataDialog",
  });

  const privacy = inject("$privacy");

  const refDemoDataDialog = ref(null);
  const refUniversalPatientAssignment = ref(null);

  const state = reactive({
    status: "loading",
    demodata: null,
    dialogWidth: "medium",
  });

  const open = () => {
    state.status = "selection";
    state.demodata = null;
    state.dialogWidth = "medium";
    refDemoDataDialog.value?.open();
  };

  const close = () => {
    refDemoDataDialog.value?.close();
    refUniversalPatientAssignment.value?.clearPatient();
  };

  const demodataSelected = (id) => {
    state.status = "loading";

    useAuthHttp().get(route("api.demodata", {id: id}), {
      onSuccess: (response) => {
        state.demodata = response;
        state.status = "universalPatientAssignment";

        nextTick(() => {
          refUniversalPatientAssignment.value.setPatient(state.demodata.patient);
        });
      },
    });
  };

  const addToExistingPatient = async (patient) => {
    state.dialogWidth = "medium";
    state.status = "saving";
    state.demodata.patient = await privacy.encryptPatient(patient);
    router.post(route("init-from-json", {origin: "demodata", patient: patient.id}), state.demodata, {
      preserveState: false, // important to force reload and re-initialization of other components
      preserveScroll: false,
      onSuccess: () => close(),
    });
  };

  const addToNewPatient = async (patient) => {
    state.dialogWidth = "medium";
    state.status = "saving";
    state.demodata.patient = await privacy.encryptPatient(patient);
    router.post(route("init-from-json", {origin: "demodata"}), state.demodata, {
      preserveState: false, // important to force reload and re-initialization of other components
      preserveScroll: false,
      onSuccess: () => close(),
    });
  };

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDemoDataDialog"
    dialog-title="Beispielfälle"
    :dialog-width="state.dialogWidth"
    dialog-type="abort"
    :test-id="getTestId('demo-data')"
  >
    <template #content>
      <div v-if="state.status === 'selection'" class="mx-4 my-10 grid grid-cols-3 gap-6">
        <ComponentDemodataDialogSelectableDemocase
          :demodata-id="1"
          name="Ursula Uebel"
          disease="Parkinson"
          :img-src="UrsulaUebel"
          :test-id="getTestId('demo-data-dialog-ursula-uebel')"
          @select="demodataSelected"
        />

        <ComponentDemodataDialogSelectableDemocase
          :demodata-id="2"
          name="Therese Torsade"
          disease="QT-Risiko"
          :img-src="ThereseTorsade"
          :test-id="getTestId('demo-data-dialog-therese-torsade')"
          @select="demodataSelected"
        />

        <ComponentDemodataDialogSelectableDemocase
          :demodata-id="3"
          name="Beate Blass"
          disease="Anämie"
          :img-src="BeateBlass"
          :test-id="getTestId('demo-data-dialog-beate-blass')"
          @select="demodataSelected"
        />
      </div>

      <div v-else-if="state.status === 'universalPatientAssignment'">
        <ComponentUniversalPatientAssignment
          ref="refUniversalPatientAssignment"
          suppress-without-match
          @existing-patient-selected="(patient) => addToExistingPatient(patient)"
          @new-patient="(patient) => addToNewPatient(patient)"
          @has-match="dialogWidth = 'large'"
        >
          <template #search-info="{hasMatch}">
            <ComponentAlert
              v-if="hasMatch"
              type="warning"
              :test-id="getTestId('demo-data-dialog-existing-patient-select')"
            >
              <span>
                Sie haben diesen Beispielfall bereits verwendet. Möchten Sie eine neue Medikationsanalyse für den
                bestehenden Patienten erstellen?
              </span>
            </ComponentAlert>
          </template>
        </ComponentUniversalPatientAssignment>
      </div>

      <div v-else-if="state.status === 'saving'" class="flex flex-col items-center justify-center py-10">
        <div class="py-4 text-lg">Wird gespeichert...</div>
        <MedSpinner size="2xl" :data-test="getTestId('demo-data-dialog-saving-spinner')" />
      </div>

      <div v-else class="flex flex-col items-center justify-center py-10">
        <MedSpinner size="2xl" :data-test="getTestId('demo-data-dialog-loading-spinner')" />
      </div>
    </template>

    <template #actions>
      <!-- <Teleport> target of UniversalPatientAssignment component (reverts space-x margin when empty) -->
      <div id="UniversalPatientAssignmentSearchButton" />

      <MedButton :data-test="getTestId('demo-data-dialog-close-button')" @click="close">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
